import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { getEnv } from "../../shared/lib/env";
import {
  LiffLoginIncompleteError,
  LiffLoginRedirectError,
  getIdTokenSafe,
  getLiffRuntimeState,
  initializeLiff,
  loginWithRedirect,
  redactSensitiveUrlForDiagnostics,
  rememberLiffLoginReturnTo,
  resetLiffInitializationState
} from "../../shared/lib/liff";
import {
  clearFlowCounter,
  clearFlowCounters,
  clearFlowForceReauthStartedAt,
  clearFlowTransientState,
  getFlowCounter,
  readFlowToken,
  readFlowForceReauthStartedAt,
  incrementFlowCounter,
  rememberFlowForceReauthStartedAt,
  rememberFlowReturnTo,
  rememberFlowTenantSlug,
  rememberFlowToken
} from "../../shared/session/flowStorage";
import { createPageErrorState, formatApiError, type PageErrorState } from "../../shared/session/flowSupport";
import {
  canReuseExistingTenantSessionForEntry,
  canShortCircuitOwnerOnboardingToOperatorRoute
} from "../../shared/session/entryOwnerOnboarding";
import { resolveEntryFlowKind } from "../../shared/session/entryFlowKind";
import { classifyEntryFailure } from "../../shared/session/inviteFlowFailure";
import { isCurrentRouteAllowedForSession } from "../../shared/session/modeRouting";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";
import type { SessionResponseDto } from "@madamuh/domain";

type EntryPhase =
  | "idle"
  | "checking-session"
  | "initializing-liff"
  | "requesting-line-login"
  | "establishing-session"
  | "checking-owner-onboarding"
  | "consuming-owner-onboarding"
  | "redirecting"
  | "error";

type EntryDiagnosticEvent = {
  at: string;
  label: string;
  detail?: string;
};

type NormalEntryRetryMode = "retryBootstrap" | "forceLineReauth";

type EntryDiagnosticsState = {
  bootstrapRunCount: number;
  lineLoginAttemptCount: number;
  lineLoginSuccessCount: number;
  reauthAttemptCount: number;
  reusedExistingSessionCount: number;
  ownerConsumeAttemptCount: number;
  ownerConsumeSuccessCount: number;
  redirectCount: number;
  lastSessionStatus: string;
  lastSessionEntryPath: string | null;
  lastSessionRoles: string[];
  lastBackendErrorCode: string | null;
  lastBackendErrorMessage: string | null;
  lastBackendDebug: Record<string, unknown> | null;
  expiredTokenDetected: boolean;
  forceReauthAttemptCount: number;
  forceReauthStartedAt: string | null;
  liffLogoutCalled: boolean;
  liffLogoutSucceeded: boolean | null;
  cleanRedirectUri: string | null;
  postReauthInitCount: number;
  postReauthHasIdToken: boolean | null;
  postReauthTokenExp: number | null;
  retryLimitReached: boolean;
  events: EntryDiagnosticEvent[];
};

const EXPIRED_LINE_TOKEN_REAUTH_MAX_ATTEMPTS = 1;

function createEntryDiagnosticsState(): EntryDiagnosticsState {
  return {
    bootstrapRunCount: 0,
    lineLoginAttemptCount: 0,
    lineLoginSuccessCount: 0,
    reauthAttemptCount: 0,
    reusedExistingSessionCount: 0,
    ownerConsumeAttemptCount: 0,
    ownerConsumeSuccessCount: 0,
    redirectCount: 0,
    lastSessionStatus: "idle",
    lastSessionEntryPath: null,
    lastSessionRoles: [],
    lastBackendErrorCode: null,
    lastBackendErrorMessage: null,
    lastBackendDebug: null,
    expiredTokenDetected: false,
    forceReauthAttemptCount: 0,
    forceReauthStartedAt: null,
    liffLogoutCalled: false,
    liffLogoutSucceeded: null,
    cleanRedirectUri: null,
    postReauthInitCount: 0,
    postReauthHasIdToken: null,
    postReauthTokenExp: null,
    retryLimitReached: false,
    events: []
  };
}

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function getLineVerifyErrorDescription(error: ApiError): string | null {
  return readNonEmptyString(error.debug?.lineVerifyErrorDescription);
}

function isExpiredLineIdTokenError(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 401 || error.code !== "INVALID_LINE_TOKEN") {
    return false;
  }

  const lineVerifyErrorDescription = getLineVerifyErrorDescription(error);
  return Boolean(lineVerifyErrorDescription && lineVerifyErrorDescription.toLowerCase().includes("idtoken expired"));
}

function extractChannelIdFromLiffId(liffId: string): string | null {
  const candidate = liffId.split("-")[0]?.trim() ?? "";
  return /^[0-9]+$/.test(candidate) ? candidate : null;
}

function readDecodedIdTokenClaims() {
  const decodedIdToken =
    typeof window !== "undefined"
      ? ((window.liff?.getDecodedIDToken?.() ?? null) as Record<string, unknown> | null)
      : null;

  return {
    audience: typeof decodedIdToken?.aud === "string" ? decodedIdToken.aud : null,
    issuer: typeof decodedIdToken?.iss === "string" ? decodedIdToken.iss : null,
    expiresAt: typeof decodedIdToken?.exp === "number" ? decodedIdToken.exp : null,
    issuedAt: typeof decodedIdToken?.iat === "number" ? decodedIdToken.iat : null
  };
}

function buildEntryUrl(tenantSlug: string, returnTo: string, ownerOnboardingToken?: string): string {
  const params = new URLSearchParams();

  if (returnTo) {
    params.set("returnTo", returnTo);
  }

  if (ownerOnboardingToken) {
    params.set("ownerOnboardingToken", ownerOnboardingToken);
  }

  const query = params.toString();
  return `/t/${tenantSlug}/entry${query ? `?${query}` : ""}`;
}

function renderEntryDiagnostics(
  diagnostics: EntryDiagnosticsState,
  entryPhase: EntryPhase,
  sessionStatus: string,
  ownerOnboardingToken: string,
  hasAttemptedBackendRecovery: boolean,
  env: ReturnType<typeof getEnv>,
  tenantSlug: string,
  returnTo: string
) {
  const decodedIdTokenClaims = readDecodedIdTokenClaims();
  const liffChannelIdPrefix = extractChannelIdFromLiffId(env.lineMiniAppLiffId);
  const backendExpectedAudience =
    typeof diagnostics.lastBackendDebug?.expectedAudience === "string"
      ? diagnostics.lastBackendDebug.expectedAudience
      : null;

  return JSON.stringify(
    {
      tenantSlug,
      appEnv: env.appEnv,
      liffId: env.lineMiniAppLiffId || null,
      liffChannelIdPrefix,
      isInClient: typeof window !== "undefined" && typeof window.liff?.isInClient === "function"
        ? window.liff.isInClient()
        : null,
      isLoggedIn: typeof window !== "undefined" && typeof window.liff?.isLoggedIn === "function"
        ? window.liff.isLoggedIn()
        : null,
      hasIdToken:
        typeof window !== "undefined" && typeof window.liff?.getIDToken === "function"
          ? Boolean(window.liff.getIDToken())
          : null,
      decodedIdTokenAud: decodedIdTokenClaims.audience,
      decodedIdTokenIss: decodedIdTokenClaims.issuer,
      decodedIdTokenExp: decodedIdTokenClaims.expiresAt,
      decodedIdTokenIat: decodedIdTokenClaims.issuedAt,
      hasOwnerOnboardingToken: Boolean(ownerOnboardingToken),
      hasAttemptedBackendRecovery,
      entryPhase,
      sessionStatus,
      returnTo,
      bootstrapRunCount: diagnostics.bootstrapRunCount,
      lineLoginAttemptCount: diagnostics.lineLoginAttemptCount,
      lineLoginSuccessCount: diagnostics.lineLoginSuccessCount,
      reauthAttemptCount: diagnostics.reauthAttemptCount,
      reusedExistingSessionCount: diagnostics.reusedExistingSessionCount,
      ownerConsumeAttemptCount: diagnostics.ownerConsumeAttemptCount,
      ownerConsumeSuccessCount: diagnostics.ownerConsumeSuccessCount,
      redirectCount: diagnostics.redirectCount,
      lastSessionStatus: diagnostics.lastSessionStatus,
      lastSessionEntryPath: diagnostics.lastSessionEntryPath,
      lastSessionRoles: diagnostics.lastSessionRoles,
      lastBackendErrorCode: diagnostics.lastBackendErrorCode,
      lastBackendErrorMessage: diagnostics.lastBackendErrorMessage,
      lastBackendDebug: diagnostics.lastBackendDebug,
      expiredTokenDetected: diagnostics.expiredTokenDetected,
      forceReauthAttemptCount: diagnostics.forceReauthAttemptCount,
      forceReauthStartedAt: diagnostics.forceReauthStartedAt,
      liffLogoutCalled: diagnostics.liffLogoutCalled,
      liffLogoutSucceeded: diagnostics.liffLogoutSucceeded,
      cleanRedirectUri: diagnostics.cleanRedirectUri,
      postReauthInitCount: diagnostics.postReauthInitCount,
      postReauthHasIdToken: diagnostics.postReauthHasIdToken,
      postReauthTokenExp: diagnostics.postReauthTokenExp,
      retryLimitReached: diagnostics.retryLimitReached,
      liffBackendAudienceMismatchSuspected:
        Boolean(liffChannelIdPrefix && backendExpectedAudience && liffChannelIdPrefix !== backendExpectedAudience),
      events: diagnostics.events
    },
    null,
    2
  );
}

function EntryErrorActions({
  errorState,
  onReturnHome,
  onRetry
}: {
  errorState: PageErrorState;
  onReturnHome: () => void;
  onRetry: () => void;
}) {
  const env = getEnv();

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <p style={{ margin: 0 }}>
        {errorState.message ?? "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。"}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={onReturnHome}>
          ホーム画面に戻る
        </button>
        {errorState.allowRetry ? (
          <button type="button" onClick={onRetry}>
            もう一度ログインする
          </button>
        ) : null}
      </div>
      {env.reviewDebugEnabled && errorState.debugMessage ? (
        <pre
          style={{
            margin: 0,
            padding: 12,
            borderRadius: 8,
            background: "#f6f6f6",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          }}
        >
          {errorState.debugMessage}
        </pre>
      ) : null}
    </div>
  );
}

function getUnavailableSessionMessage(session: SessionResponseDto | null): string | null {
  if (!session || session.accessState === "active" || session.accessState === "preMember") {
    return null;
  }

  if (session.accessState === "noVisibleStudents") {
    return session.accessMessage ?? "現在閲覧できる生徒がいません。管理者にお問い合わせください。";
  }

  return session.accessMessage ?? "このアカウントは現在利用できません。管理者にお問い合わせください。";
}

function EntryUnavailableState({
  session,
  onReturnTop
}: {
  session: SessionResponseDto;
  onReturnTop: () => void;
}) {
  const message = getUnavailableSessionMessage(session);

  if (!message) {
    return null;
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <p style={{ margin: 0 }}>{message}</p>
      <div>
        <button type="button" onClick={onReturnTop}>
          TOPへ戻る
        </button>
      </div>
    </div>
  );
}

function MockEntryPage({
  tenantSlug,
  returnTo
}: {
  tenantSlug: string;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const { status, session, errorMessage, bootstrapSession, lineLoginWithMockToken } = useSessionBootstrap();

  useEffect(() => {
    void bootstrapSession(tenantSlug);
  }, [bootstrapSession, tenantSlug]);

  useEffect(() => {
    if (status === "authenticated" && session && session.accessState === "active") {
      navigate(
        returnTo && isCurrentRouteAllowedForSession(returnTo, session) ? returnTo : session.entryPath,
        { replace: true }
      );
    }
  }, [navigate, returnTo, session, status]);

  if (status === "authenticated" && session?.accessState === "active") {
    return (
      <Navigate
        to={returnTo && isCurrentRouteAllowedForSession(returnTo, session) ? returnTo : session.entryPath}
        replace
      />
    );
  }

  return (
    <PageScaffold
      title="利用開始"
      description="LINE認証後の分岐画面です。local/dev では mock session bootstrap を使って preMember / family / operator の振り分けを確認できます。"
    >
      {status === "authenticated" && session?.accessState !== "active" && session ? (
        <EntryUnavailableState session={session} onReturnTop={() => navigate(`/t/${tenantSlug}`, { replace: true })} />
      ) : null}
      {status === "loading" ? <p>セッションを確認しています。</p> : null}
      {status === "unauthenticated" ? <p>未ログインです。local/dev では mock LINE login で session bootstrap を確認できます。</p> : null}
      {status === "error" ? <p>セッション取得に失敗しました。{errorMessage}</p> : null}
      <div style={{ display: "grid", gap: 12 }}>
        <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-pre-member-token")}>
          Mock Login: preMember
        </button>
        <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-parent-token")}>
          Mock Login: family
        </button>
        <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-operator-token")}>
          Mock Login: operator
        </button>
        <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-teacher-token")}>
          Mock Login: teacher
        </button>
      </div>
    </PageScaffold>
  );
}

function NormalEntryFlow({
  tenantSlug,
  returnTo
}: {
  tenantSlug: string;
  returnTo: string;
}) {
  const env = getEnv();
  const navigate = useNavigate();
  const {
    status,
    session,
    errorMessage,
    establishSessionWithLineToken,
    resetBootstrapState,
    verifySession
  } = useSessionBootstrap();
  const [entryPhase, setEntryPhase] = useState<EntryPhase>("idle");
  const [entryErrorState, setEntryErrorState] = useState<PageErrorState>(createPageErrorState);
  const [entryRetryMode, setEntryRetryMode] = useState<NormalEntryRetryMode>("retryBootstrap");
  const [retryNonce, setRetryNonce] = useState(0);
  const [diagnostics, setDiagnostics] = useState<EntryDiagnosticsState>(createEntryDiagnosticsState);
  const latestSessionRef = useRef(session);
  const bootstrapRunKeyRef = useRef<string | null>(null);
  const tenantTopPath = `/t/${tenantSlug}`;
  const currentEntryUrl = buildEntryUrl(tenantSlug, returnTo);

  const pushDiagnosticEvent = useRef((label: string, detail?: string) => {
    setDiagnostics((current) => ({
      ...current,
      events: [
        ...current.events,
        detail === undefined
          ? { at: new Date().toISOString(), label }
          : { at: new Date().toISOString(), label, detail }
      ].slice(-12)
    }));
  }).current;

  useEffect(() => {
    latestSessionRef.current = session;
    setDiagnostics((current) => ({
      ...current,
      lastSessionStatus: status,
      lastSessionEntryPath: session?.entryPath ?? null,
      lastSessionRoles: session?.roles ?? [],
      lastBackendErrorMessage: errorMessage,
      lastBackendErrorCode:
        errorMessage && errorMessage.startsWith("HTTP ")
          ? errorMessage.split("\n").find((line) => line.startsWith("code: "))?.slice(6) ?? null
          : current.lastBackendErrorCode
    }));
  }, [errorMessage, session, status]);

  async function forceLineReauthForNormalEntry() {
    const existingAttemptCount = getFlowCounter("normalEntry", "expiredTokenReauthAttemptCount");

    if (existingAttemptCount >= EXPIRED_LINE_TOKEN_REAUTH_MAX_ATTEMPTS) {
      setDiagnostics((current) => ({
        ...current,
        retryLimitReached: true,
        forceReauthAttemptCount: existingAttemptCount
      }));
      setEntryRetryMode("retryBootstrap");
      setEntryPhase("error");
      setEntryErrorState({
        message: "ログインに失敗しました。ホーム画面に戻り、LINEアプリを完全に閉じてからもう一度お試しください。",
        debugMessage: null,
        allowRetry: false
      });
      return;
    }

    const cleanRedirectUri = currentEntryUrl;
    const cleanRedirectUriForDiagnostics =
      redactSensitiveUrlForDiagnostics(cleanRedirectUri) ?? cleanRedirectUri;
    const forceReauthStartedAt = new Date().toISOString();

    clearFlowCounter("normalEntry", "loginAttemptCount");
    clearFlowCounter("normalEntry", "backendRecoveryAttemptCount");
    clearFlowForceReauthStartedAt("normalEntry");
    rememberFlowTenantSlug("normalEntry", tenantSlug);
    rememberFlowReturnTo("normalEntry", cleanRedirectUri);
    rememberLiffLoginReturnTo(cleanRedirectUri);
    rememberFlowForceReauthStartedAt("normalEntry", forceReauthStartedAt);
    const nextAttemptCount = incrementFlowCounter("normalEntry", "expiredTokenReauthAttemptCount");

    resetBootstrapState();
    resetLiffInitializationState();
    setEntryRetryMode("retryBootstrap");
    setEntryErrorState(createPageErrorState());
    setEntryPhase("requesting-line-login");
    setDiagnostics((current) => ({
      ...current,
      expiredTokenDetected: true,
      forceReauthAttemptCount: nextAttemptCount,
      forceReauthStartedAt,
      cleanRedirectUri: cleanRedirectUriForDiagnostics,
      liffLogoutCalled: false,
      liffLogoutSucceeded: null,
      retryLimitReached: nextAttemptCount >= EXPIRED_LINE_TOKEN_REAUTH_MAX_ATTEMPTS
    }));
    pushDiagnosticEvent("force-reauth-start", cleanRedirectUriForDiagnostics);

    const liff = await initializeLiff(env.lineMiniAppLiffId);
    const isLoggedIn = liff.isLoggedIn();
    const isInClient = typeof liff.isInClient === "function" ? liff.isInClient() : null;
    let logoutCalled = false;
    let logoutSucceeded: boolean | null = null;

    if (isLoggedIn && typeof liff.logout === "function") {
      logoutCalled = true;

      try {
        liff.logout();
        logoutSucceeded = true;
      } catch {
        logoutSucceeded = false;
      }
    }

    setDiagnostics((current) => ({
      ...current,
      liffLogoutCalled: logoutCalled,
      liffLogoutSucceeded: logoutSucceeded
    }));
    pushDiagnosticEvent(
      "force-reauth-logout",
      JSON.stringify({
        isInClient,
        isLoggedIn,
        logoutCalled,
        logoutSucceeded
      })
    );

    resetLiffInitializationState();

    if (isInClient) {
      window.location.replace(cleanRedirectUri);
      throw new LiffLoginRedirectError();
    }

    await loginWithRedirect(env.lineMiniAppLiffId, cleanRedirectUri);
  }

  function handleReturnToTenantTop() {
    clearFlowTransientState("normalEntry");
    setEntryErrorState(createPageErrorState());
    setEntryRetryMode("retryBootstrap");
    bootstrapRunKeyRef.current = null;
    navigate(tenantTopPath, { replace: true });
  }

  function handleRetryLogin() {
    if (entryRetryMode === "forceLineReauth") {
      void forceLineReauthForNormalEntry().catch((error: unknown) => {
        if (error instanceof LiffLoginRedirectError) {
          return;
        }

        setEntryPhase("error");
        setEntryRetryMode("retryBootstrap");
        setEntryErrorState({
          message: "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。",
          debugMessage: formatApiError(error),
          allowRetry: true
        });
        pushDiagnosticEvent("force-reauth-error", formatApiError(error));
      });
      return;
    }

    clearFlowTransientState("normalEntry");
    setEntryRetryMode("retryBootstrap");
    setEntryErrorState(createPageErrorState());
    setDiagnostics(createEntryDiagnosticsState());
    bootstrapRunKeyRef.current = null;
    setEntryPhase("idle");
    setRetryNonce((current) => current + 1);
  }

  useEffect(() => {
    const bootstrapRunKey = JSON.stringify({
      tenantSlug,
      returnTo,
      retryNonce
    });

    if (bootstrapRunKeyRef.current === bootstrapRunKey) {
      return;
    }

    bootstrapRunKeyRef.current = bootstrapRunKey;
    const expiredTokenReauthAttemptCount = getFlowCounter("normalEntry", "expiredTokenReauthAttemptCount");
    const storedForceReauthStartedAt = readFlowForceReauthStartedAt("normalEntry");
    setDiagnostics((current) => ({
      ...current,
      bootstrapRunCount: current.bootstrapRunCount + 1,
      forceReauthAttemptCount: expiredTokenReauthAttemptCount,
      forceReauthStartedAt: storedForceReauthStartedAt,
      retryLimitReached: expiredTokenReauthAttemptCount >= EXPIRED_LINE_TOKEN_REAUTH_MAX_ATTEMPTS
    }));
    pushDiagnosticEvent("bootstrap-start", bootstrapRunKey);

    let cancelled = false;

    async function runFlow() {
      setEntryErrorState(createPageErrorState());
      setEntryRetryMode("retryBootstrap");
      setEntryPhase("initializing-liff");

      try {
        const nextSessionCandidate = latestSessionRef.current;

        if (canReuseExistingTenantSessionForEntry(tenantSlug, nextSessionCandidate)) {
          const reusableSession = nextSessionCandidate;

          if (!reusableSession) {
            throw new Error("Tenant session reuse failed");
          }

          setDiagnostics((current) => ({
            ...current,
            reusedExistingSessionCount: current.reusedExistingSessionCount + 1
          }));

          if (reusableSession.accessState !== "active") {
            clearFlowTransientState("normalEntry");
            pushDiagnosticEvent("reuse-existing-session-unavailable", reusableSession.accessState);
            setEntryPhase("idle");
            return;
          }

          pushDiagnosticEvent("reuse-existing-session", reusableSession.entryPath);
          clearFlowTransientState("normalEntry");
          setEntryPhase("redirecting");
          navigate(
            returnTo && isCurrentRouteAllowedForSession(returnTo, reusableSession)
              ? returnTo
              : reusableSession.entryPath,
            { replace: true }
          );
          return;
        }

        const runtimeState = await getLiffRuntimeState(env.lineMiniAppLiffId);

        if (expiredTokenReauthAttemptCount > 0) {
          setDiagnostics((current) => ({
            ...current,
            postReauthInitCount: current.postReauthInitCount + 1,
            postReauthHasIdToken: runtimeState.hasIdToken
          }));
        }

        if (!runtimeState.isLoggedIn) {
          const loginAttemptCount = getFlowCounter("normalEntry", "loginAttemptCount");

          if (loginAttemptCount >= 1) {
            throw new LiffLoginIncompleteError();
          }

          incrementFlowCounter("normalEntry", "loginAttemptCount");
          rememberFlowTenantSlug("normalEntry", tenantSlug);
          rememberFlowReturnTo("normalEntry", currentEntryUrl);
          rememberLiffLoginReturnTo(currentEntryUrl);
          setDiagnostics((current) => ({
            ...current,
            lineLoginAttemptCount: current.lineLoginAttemptCount + 1
          }));
          pushDiagnosticEvent("line-login-start", `tenant=${tenantSlug}`);
          setEntryPhase("requesting-line-login");
          await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
          return;
        }

        const idToken = await getIdTokenSafe(env.lineMiniAppLiffId);

        if (expiredTokenReauthAttemptCount > 0) {
          const decodedIdTokenClaims = readDecodedIdTokenClaims();
          setDiagnostics((current) => ({
            ...current,
            postReauthHasIdToken: Boolean(idToken),
            postReauthTokenExp: decodedIdTokenClaims.expiresAt
          }));
        }

        if (!idToken) {
          const loginAttemptCount = getFlowCounter("normalEntry", "loginAttemptCount");

          if (loginAttemptCount >= 1) {
            throw new LiffLoginIncompleteError();
          }

          incrementFlowCounter("normalEntry", "loginAttemptCount");
          rememberFlowTenantSlug("normalEntry", tenantSlug);
          rememberFlowReturnTo("normalEntry", currentEntryUrl);
          rememberLiffLoginReturnTo(currentEntryUrl);
          setDiagnostics((current) => ({
            ...current,
            lineLoginAttemptCount: current.lineLoginAttemptCount + 1
          }));
          pushDiagnosticEvent("line-login-start", `tenant=${tenantSlug}`);
          setEntryPhase("requesting-line-login");
          await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
          return;
        }

        if (cancelled) {
          return;
        }

        setDiagnostics((current) => ({
          ...current,
          lineLoginSuccessCount: current.lineLoginSuccessCount + 1
        }));
        pushDiagnosticEvent("line-login-success");
        setEntryPhase("establishing-session");
        const nextSession = await establishSessionWithLineToken(tenantSlug, idToken);

        if (cancelled) {
          return;
        }

        if (nextSession.accessState !== "active") {
          clearFlowTransientState("normalEntry");
          pushDiagnosticEvent("session-established-unavailable", nextSession.accessState);
          setEntryPhase("idle");
          return;
        }

        clearFlowTransientState("normalEntry");
        setEntryPhase("redirecting");
        setDiagnostics((current) => ({
          ...current,
          redirectCount: current.redirectCount + 1
        }));
        pushDiagnosticEvent("navigate", nextSession.entryPath);
        navigate(
          returnTo && isCurrentRouteAllowedForSession(returnTo, nextSession)
            ? returnTo
            : nextSession.entryPath,
          { replace: true }
        );
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof LiffLoginRedirectError) {
          return;
        }

        if (isExpiredLineIdTokenError(error)) {
          setDiagnostics((current) => ({
            ...current,
            expiredTokenDetected: true,
            lastBackendDebug: error.debug
          }));
          pushDiagnosticEvent(
            "expired-line-id-token",
            getLineVerifyErrorDescription(error) ?? "LINE verify returned expired token"
          );

          setEntryPhase("checking-session");
          const verifiedSession = await verifySession(tenantSlug);

          if (cancelled) {
            return;
          }

          if (verifiedSession.status === "authenticated") {
            if (verifiedSession.session.accessState !== "active") {
              clearFlowTransientState("normalEntry");
              pushDiagnosticEvent(
                "reuse-session-after-expired-token-unavailable",
                verifiedSession.session.accessState
              );
              setEntryPhase("idle");
              return;
            }

            clearFlowTransientState("normalEntry");
            setEntryPhase("redirecting");
            setDiagnostics((current) => ({
              ...current,
              reusedExistingSessionCount: current.reusedExistingSessionCount + 1,
              redirectCount: current.redirectCount + 1
            }));
            pushDiagnosticEvent("reuse-session-after-expired-token", verifiedSession.session.entryPath);
            navigate(
              returnTo && isCurrentRouteAllowedForSession(returnTo, verifiedSession.session)
                ? returnTo
                : verifiedSession.session.entryPath,
              { replace: true }
            );
            return;
          }

          const retryLimitReached =
            expiredTokenReauthAttemptCount >= EXPIRED_LINE_TOKEN_REAUTH_MAX_ATTEMPTS;

          setDiagnostics((current) => ({
            ...current,
            retryLimitReached
          }));
          setEntryRetryMode("forceLineReauth");
          setEntryPhase("error");
          setEntryErrorState({
            message: retryLimitReached
              ? "ログインに失敗しました。ホーム画面に戻り、LINEアプリを完全に閉じてからもう一度お試しください。"
              : "ログインの有効期限が切れました。もう一度ログインしてください。",
            debugMessage: formatApiError(error),
            allowRetry: !retryLimitReached
          });
          return;
        }

        if (error instanceof ApiError && error.code === "INVALID_LINE_TOKEN") {
          const backendRecoveryAttemptCount = getFlowCounter("normalEntry", "backendRecoveryAttemptCount");

          if (backendRecoveryAttemptCount < 1) {
            incrementFlowCounter("normalEntry", "backendRecoveryAttemptCount");
            rememberFlowTenantSlug("normalEntry", tenantSlug);
            rememberFlowReturnTo("normalEntry", currentEntryUrl);
            rememberLiffLoginReturnTo(currentEntryUrl);
            setDiagnostics((current) => ({
              ...current,
              reauthAttemptCount: current.reauthAttemptCount + 1
            }));
            pushDiagnosticEvent("line-reauth-start", currentEntryUrl);
            setEntryPhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
            return;
          }
        }

        setDiagnostics((current) => ({
          ...current,
          lastBackendDebug: error instanceof ApiError ? error.debug : null
        }));
        setEntryPhase("error");
        setEntryRetryMode("retryBootstrap");
        pushDiagnosticEvent("entry-error", formatApiError(error));
        const entryFailure = classifyEntryFailure(error, false);
        setEntryErrorState({
          message: entryFailure.userMessage,
          debugMessage: formatApiError(error),
          allowRetry: entryFailure.allowRetry
        });
      }
    }

    void runFlow();

    return () => {
      cancelled = true;
    };
  }, [
    currentEntryUrl,
    env.lineMiniAppLiffId,
    establishSessionWithLineToken,
    navigate,
    pushDiagnosticEvent,
    retryNonce,
    returnTo,
    tenantSlug,
    verifySession
  ]);

  return (
    <PageScaffold
      title="利用開始"
      description="LINE認証後の分岐画面です。LIFF で LINE ID token を取得し、Cloud Run API で session bootstrap を行います。"
    >
      {status === "authenticated" && session?.accessState !== "active" && session ? (
        <EntryUnavailableState session={session} onReturnTop={handleReturnToTenantTop} />
      ) : null}
      {entryPhase === "checking-session" ? <p>セッションを確認しています。</p> : null}
      {entryPhase === "initializing-liff" ? <p>LIFFを初期化しています。</p> : null}
      {entryPhase === "requesting-line-login" ? <p>LINEログインを確認しています。</p> : null}
      {entryPhase === "establishing-session" ? <p>セッションを作成しています。</p> : null}
      {entryPhase === "redirecting" ? <p>画面を移動しています。</p> : null}
      {entryPhase === "error" ? (
        <EntryErrorActions
          errorState={entryErrorState}
          onReturnHome={handleReturnToTenantTop}
          onRetry={handleRetryLogin}
        />
      ) : null}

      {env.reviewDebugEnabled ? (
        <pre
          style={{
            margin: 0,
            padding: 12,
            borderRadius: 8,
            background: "#f6f6f6",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          }}
        >
          {renderEntryDiagnostics(
            diagnostics,
            entryPhase,
            status,
            "",
            getFlowCounter("normalEntry", "backendRecoveryAttemptCount") > 0,
            env,
            tenantSlug,
            returnTo
          )}
        </pre>
      ) : null}

      <p style={{ margin: 0, opacity: 0.8 }}>LIFF と backend session bootstrap により、権限に応じた画面へ自動で移動します。</p>
    </PageScaffold>
  );
}

function OwnerOnboardingEntryFlow({
  tenantSlug,
  returnTo,
  ownerOnboardingToken,
  ownerOnboardingTokenFromQuery,
  hasOwnerOnboardingTokenQueryParam
}: {
  tenantSlug: string;
  returnTo: string;
  ownerOnboardingToken: string;
  ownerOnboardingTokenFromQuery: string;
  hasOwnerOnboardingTokenQueryParam: boolean;
}) {
  const env = getEnv();
  const navigate = useNavigate();
  const { status, session, errorMessage, establishSessionWithLineToken, consumeOwnerOnboardingToken } =
    useSessionBootstrap();
  const [entryPhase, setEntryPhase] = useState<EntryPhase>("idle");
  const [entryErrorState, setEntryErrorState] = useState<PageErrorState>(createPageErrorState);
  const [retryNonce, setRetryNonce] = useState(0);
  const [diagnostics, setDiagnostics] = useState<EntryDiagnosticsState>(createEntryDiagnosticsState);
  const latestSessionRef = useRef(session);
  const bootstrapRunKeyRef = useRef<string | null>(null);
  const tenantTopPath = `/t/${tenantSlug}`;
  const currentEntryUrl = buildEntryUrl(tenantSlug, returnTo, ownerOnboardingToken);

  const pushDiagnosticEvent = useRef((label: string, detail?: string) => {
    setDiagnostics((current) => ({
      ...current,
      events: [
        ...current.events,
        detail === undefined
          ? { at: new Date().toISOString(), label }
          : { at: new Date().toISOString(), label, detail }
      ].slice(-12)
    }));
  }).current;

  useEffect(() => {
    latestSessionRef.current = session;
    setDiagnostics((current) => ({
      ...current,
      lastSessionStatus: status,
      lastSessionEntryPath: session?.entryPath ?? null,
      lastSessionRoles: session?.roles ?? [],
      lastBackendErrorMessage: errorMessage,
      lastBackendErrorCode:
        errorMessage && errorMessage.startsWith("HTTP ")
          ? errorMessage.split("\n").find((line) => line.startsWith("code: "))?.slice(6) ?? null
          : current.lastBackendErrorCode
    }));
  }, [errorMessage, session, status]);

  function handleReturnToTenantTop() {
    clearFlowTransientState("ownerOnboarding");
    setEntryErrorState(createPageErrorState());
    bootstrapRunKeyRef.current = null;
    navigate(tenantTopPath, { replace: true });
  }

  function handleRetryLogin() {
    clearFlowCounters("ownerOnboarding");
    setEntryErrorState(createPageErrorState());
    setDiagnostics(createEntryDiagnosticsState());
    bootstrapRunKeyRef.current = null;
    setEntryPhase("idle");
    setRetryNonce((current) => current + 1);
  }

  useEffect(() => {
    const bootstrapRunKey = JSON.stringify({
      tenantSlug,
      returnTo,
      hasOwnerOnboardingToken: Boolean(ownerOnboardingToken),
      hasOwnerOnboardingTokenFromQuery: Boolean(ownerOnboardingTokenFromQuery),
      hasOwnerOnboardingTokenQueryParam,
      retryNonce
    });

    if (bootstrapRunKeyRef.current === bootstrapRunKey) {
      return;
    }

    bootstrapRunKeyRef.current = bootstrapRunKey;
    setDiagnostics((current) => ({
      ...current,
      bootstrapRunCount: current.bootstrapRunCount + 1
    }));
    pushDiagnosticEvent("bootstrap-start", bootstrapRunKey);

    let cancelled = false;

    async function runFlow() {
      if (hasOwnerOnboardingTokenQueryParam && !ownerOnboardingTokenFromQuery) {
        setEntryPhase("error");
        setEntryErrorState({
          message: "オーナー初期設定トークンが見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        clearFlowTransientState("ownerOnboarding");
        return;
      }

      if (!ownerOnboardingToken) {
        setEntryPhase("error");
        setEntryErrorState({
          message: "オーナー初期設定トークンが見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        return;
      }

      rememberFlowToken("ownerOnboarding", ownerOnboardingToken);
      rememberFlowTenantSlug("ownerOnboarding", tenantSlug);
      rememberFlowReturnTo("ownerOnboarding", currentEntryUrl);
      rememberLiffLoginReturnTo(currentEntryUrl);
      setEntryErrorState(createPageErrorState());
      setEntryPhase("initializing-liff");

      try {
        let nextSession = latestSessionRef.current;

        if (canReuseExistingTenantSessionForEntry(tenantSlug, nextSession)) {
          const reusableSession = nextSession;

          if (!reusableSession) {
            throw new Error("Tenant session reuse failed");
          }

          setDiagnostics((current) => ({
            ...current,
            reusedExistingSessionCount: current.reusedExistingSessionCount + 1
          }));
          pushDiagnosticEvent(
            "reuse-existing-session",
            JSON.stringify({
              entryPath: reusableSession.entryPath,
              roles: reusableSession.roles
            })
          );
        } else {
          const runtimeState = await getLiffRuntimeState(env.lineMiniAppLiffId);

          if (!runtimeState.isLoggedIn) {
            const loginAttemptCount = getFlowCounter("ownerOnboarding", "loginAttemptCount");

            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter("ownerOnboarding", "loginAttemptCount");
            setDiagnostics((current) => ({
              ...current,
              lineLoginAttemptCount: current.lineLoginAttemptCount + 1
            }));
            pushDiagnosticEvent("line-login-start", `tenant=${tenantSlug}`);
            setEntryPhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
            return;
          }

          const idToken = await getIdTokenSafe(env.lineMiniAppLiffId);

          if (!idToken) {
            const loginAttemptCount = getFlowCounter("ownerOnboarding", "loginAttemptCount");

            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter("ownerOnboarding", "loginAttemptCount");
            setDiagnostics((current) => ({
              ...current,
              lineLoginAttemptCount: current.lineLoginAttemptCount + 1
            }));
            pushDiagnosticEvent("line-login-start", `tenant=${tenantSlug}`);
            setEntryPhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
            return;
          }

          if (cancelled) {
            return;
          }

          setDiagnostics((current) => ({
            ...current,
            lineLoginSuccessCount: current.lineLoginSuccessCount + 1
          }));
          pushDiagnosticEvent("line-login-success");
          setEntryPhase("establishing-session");
          nextSession = await establishSessionWithLineToken(tenantSlug, idToken);
        }

        if (cancelled) {
          return;
        }

        if (canShortCircuitOwnerOnboardingToOperatorRoute(tenantSlug, nextSession, ownerOnboardingToken)) {
          const reusableOperatorSession = nextSession;
          clearFlowTransientState("ownerOnboarding");
          setEntryPhase("redirecting");
          setDiagnostics((current) => ({
            ...current,
            redirectCount: current.redirectCount + 1
          }));
          pushDiagnosticEvent("owner-short-circuit-navigate", reusableOperatorSession.entryPath);
          navigate(
            returnTo && isCurrentRouteAllowedForSession(returnTo, reusableOperatorSession)
              ? returnTo
              : reusableOperatorSession.entryPath,
            { replace: true }
          );
          return;
        }

        setEntryPhase("checking-owner-onboarding");
        await Promise.resolve();

        if (cancelled) {
          return;
        }

        setEntryPhase("consuming-owner-onboarding");
        setDiagnostics((current) => ({
          ...current,
          ownerConsumeAttemptCount: current.ownerConsumeAttemptCount + 1
        }));
        pushDiagnosticEvent("owner-consume-start");
        nextSession = await consumeOwnerOnboardingToken(tenantSlug, ownerOnboardingToken);
        setDiagnostics((current) => ({
          ...current,
          ownerConsumeSuccessCount: current.ownerConsumeSuccessCount + 1
        }));
        pushDiagnosticEvent(
          "owner-consume-success",
          JSON.stringify({
            entryPath: nextSession.entryPath,
            roles: nextSession.roles
          })
        );

        if (cancelled) {
          return;
        }

        clearFlowTransientState("ownerOnboarding");
        setEntryPhase("redirecting");
        setDiagnostics((current) => ({
          ...current,
          redirectCount: current.redirectCount + 1
        }));
        pushDiagnosticEvent("navigate", nextSession.entryPath);
        navigate(
          returnTo && isCurrentRouteAllowedForSession(returnTo, nextSession)
            ? returnTo
            : nextSession.entryPath,
          { replace: true }
        );
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof LiffLoginRedirectError) {
          return;
        }

        if (error instanceof ApiError && error.code === "INVALID_LINE_TOKEN") {
          const backendRecoveryAttemptCount = getFlowCounter("ownerOnboarding", "backendRecoveryAttemptCount");

          if (backendRecoveryAttemptCount < 1) {
            incrementFlowCounter("ownerOnboarding", "backendRecoveryAttemptCount");
            rememberFlowToken("ownerOnboarding", ownerOnboardingToken);
            rememberFlowTenantSlug("ownerOnboarding", tenantSlug);
            rememberFlowReturnTo("ownerOnboarding", currentEntryUrl);
            rememberLiffLoginReturnTo(currentEntryUrl);
            setDiagnostics((current) => ({
              ...current,
              reauthAttemptCount: current.reauthAttemptCount + 1
            }));
            pushDiagnosticEvent("line-reauth-start", currentEntryUrl);
            setEntryPhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentEntryUrl);
            return;
          }
        }

        setDiagnostics((current) => ({
          ...current,
          lastBackendDebug: error instanceof ApiError ? error.debug : null
        }));
        const entryFailure = classifyEntryFailure(error, true);
        setEntryPhase("error");
        pushDiagnosticEvent("entry-error", formatApiError(error));
        setEntryErrorState({
          message: entryFailure.userMessage,
          debugMessage: formatApiError(error),
          allowRetry: entryFailure.allowRetry
        });
      }
    }

    void runFlow();

    return () => {
      cancelled = true;
    };
  }, [
    consumeOwnerOnboardingToken,
    currentEntryUrl,
    env.lineMiniAppLiffId,
    establishSessionWithLineToken,
    hasOwnerOnboardingTokenQueryParam,
    navigate,
    ownerOnboardingToken,
    ownerOnboardingTokenFromQuery,
    pushDiagnosticEvent,
    retryNonce,
    returnTo,
    tenantSlug
  ]);

  return (
    <PageScaffold
      title="利用開始"
      description="LINE認証後の分岐画面です。LIFF で LINE ID token を取得し、Cloud Run API で session bootstrap を行います。"
    >
      {entryPhase === "initializing-liff" ? <p>LIFFを初期化しています。</p> : null}
      {entryPhase === "requesting-line-login" ? <p>LINEログインを確認しています。</p> : null}
      {entryPhase === "establishing-session" ? <p>セッションを作成しています。</p> : null}
      {entryPhase === "checking-owner-onboarding" ? <p>オーナー初期設定トークンを確認しています。</p> : null}
      {entryPhase === "consuming-owner-onboarding" ? <p>管理者権限を設定しています。</p> : null}
      {entryPhase === "redirecting" ? <p>管理画面へ移動しています。</p> : null}
      {entryPhase === "error" ? (
        <EntryErrorActions
          errorState={entryErrorState}
          onReturnHome={handleReturnToTenantTop}
          onRetry={handleRetryLogin}
        />
      ) : null}

      {env.reviewDebugEnabled ? (
        <pre
          style={{
            margin: 0,
            padding: 12,
            borderRadius: 8,
            background: "#f6f6f6",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          }}
        >
          {renderEntryDiagnostics(
            diagnostics,
            entryPhase,
            status,
            ownerOnboardingToken,
            getFlowCounter("ownerOnboarding", "backendRecoveryAttemptCount") > 0,
            env,
            tenantSlug,
            returnTo
          )}
        </pre>
      ) : null}

      <p style={{ margin: 0, opacity: 0.8 }}>LIFF と backend session bootstrap により、権限に応じた画面へ自動で移動します。</p>
    </PageScaffold>
  );
}

export function EntryPage() {
  const env = getEnv();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo")?.trim() ?? "";
  const hasOwnerOnboardingTokenQueryParam = searchParams.has("ownerOnboardingToken");
  const ownerOnboardingTokenFromQuery = searchParams.get("ownerOnboardingToken")?.trim() ?? "";
  const ownerOnboardingToken = ownerOnboardingTokenFromQuery || readFlowToken("ownerOnboarding", tenantSlug) || "";
  const flowKind = resolveEntryFlowKind(ownerOnboardingToken, hasOwnerOnboardingTokenQueryParam);

  if (env.enableMock && flowKind === "normalEntry") {
    return <MockEntryPage tenantSlug={tenantSlug} returnTo={returnTo} />;
  }

  if (flowKind === "ownerOnboarding") {
    return (
      <OwnerOnboardingEntryFlow
        tenantSlug={tenantSlug}
        returnTo={returnTo}
        ownerOnboardingToken={ownerOnboardingToken}
        ownerOnboardingTokenFromQuery={ownerOnboardingTokenFromQuery}
        hasOwnerOnboardingTokenQueryParam={hasOwnerOnboardingTokenQueryParam}
      />
    );
  }

  return <NormalEntryFlow tenantSlug={tenantSlug} returnTo={returnTo} />;
}
